<template>
    <div class="news-list-container">
        <!-- 页面标题 -->
        <div class="page-title">
            <h2>新闻资讯</h2>
            <p class="subtitle">及时了解各领域最新动态</p>
        </div>

        <!-- 筛选与搜索区 -->
        <div class="filter-search-bar">
            <!-- 新闻类别筛选 -->
            <el-select v-model="selectedType" placeholder="选择新闻类别" class="filter-select" @change="handleFilterChange">
                <el-option label="全部类别" value=""></el-option>
                <el-option v-for="type in allTypes" :key="type" :label="type" :value="type"></el-option>
            </el-select>

            <!-- 新闻标题搜索 -->
            <el-input v-model="searchQuery" placeholder="搜索新闻标题..." prefix-icon="search" clearable class="search-input"
                @input="handleSearch" @clear="handleSearch"></el-input>
        </div>

        <!-- 加载状态 -->
        <el-loading v-if="loading" fullscreen text="正在加载新闻数据..." spinner="el-icon-loading"
            background="rgba(255,255,255,0.9)"></el-loading>

        <!-- 错误提示 -->
        <el-alert v-else-if="error" title="数据加载失败" :description="error" type="error" show-icon
            class="error-alert"></el-alert>

        <!-- 新闻卡片列表 -->
        <div class="news-card-list" v-else-if="filteredNews.length > 0">
            <el-card v-for="(news, index) in currentPageNews" :key="news.id" class="news-card" :hover="true"
                @click="goToDetail(news)">
                <div class="card-content">
                    <!-- 新闻封面图 -->
                    <div class="news-cover-container">
                        <img :src="news.newsCover" :alt="news.newsTitle" class="news-cover" @error="handleCoverError">
                    </div>

                    <!-- 新闻信息（标题+发布时间） -->
                    <div class="news-info">
                        <h3 class="news-title">
                            <!-- 类别1：外部链接新闻 → 标题可点击跳转 -->
                            <a v-if="news.newsCategory === 1 && news.newsLink" :href="news.newsLink" target="_blank"
                                rel="noopener noreferrer">
                                {{ news.newsTitle }}
                            </a>
                            <!-- 类别2：管理员上传新闻 → 标题仅展示 -->
                            <span v-else class="internal-news">{{ news.newsTitle }}</span>
                        </h3>
                        <div class="news-meta">
                            <i class="el-icon-time time-icon"></i>
                            <span class="publish-time">{{ formatTime(news.publishTime) }}</span>
                            <span class="news-type-tag" :style="{ backgroundColor: getTypeColor(news.newsType) }">
                                {{ news.newsType }}
                            </span>
                        </div>
                    </div>
                </div>
            </el-card>
        </div>

        <!-- 无数据提示 -->
        <el-empty v-else description="未找到匹配的新闻" class="no-news-tip"></el-empty>

        <!-- 分页组件 -->
        <div class="pagination-container" v-if="filteredNews.length > 0">
            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
                :current-page="currentPage" :page-sizes="[10, 20, 30]" :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper" :total="filteredNews.length"></el-pagination>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NewsList',
    data() {
        return {
            newsList: [], // 原始新闻列表
            loading: false, // 加载状态
            error: '', // 错误信息
            searchQuery: '', // 搜索关键词
            selectedType: '', // 选中的新闻类别
            allTypes: [], // 所有新闻类别
            currentPage: 1, // 当前页码
            pageSize: 10 // 每页条数
        }
    },
    computed: {
        // 筛选后的新闻列表（搜索+类别筛选）
        filteredNews() {
            return this.newsList.filter(news => {
                const matchTitle = news.newsTitle.toLowerCase().includes(this.searchQuery.toLowerCase())
                const matchType = this.selectedType ? news.newsType === this.selectedType : true
                return matchTitle && matchType
            })
        },
        // 当前页显示的新闻
        currentPageNews() {
            const startIndex = (this.currentPage - 1) * this.pageSize
            const endIndex = startIndex + this.pageSize
            return this.filteredNews.slice(startIndex, endIndex)
        }
    },
    mounted() {
        // 组件挂载时加载数据
        this.loadNewsData()
    },
    methods: {
        // 加载新闻数据
        async loadNewsData() {
            this.loading = true
            this.error = ''
            try {
                // Vue2中通常将axios挂载到Vue原型上
                const response = await this.$httpTemp.get('/mock/news.json')
                this.newsList = response

                // 提取所有不重复的新闻类别
                const typeSet = new Set()
                this.newsList.forEach(news => typeSet.add(news.newsType))
                this.allTypes = Array.from(typeSet)
            } catch (err) {
                this.error = '新闻数据加载失败，请稍后重试'
                console.error('加载新闻错误：', err)
            } finally {
                this.loading = false
            }
        },
        // 跳转到详情页（仅适用于类型2的新闻）
        goToDetail(news) {
            //非链接新闻
            if (news.newsCategory == 2) {
                this.$router.push({
                    name: 'NewsDetail',
                    params: { id: news.id }
                })
            }

        },
        // 类别筛选变化
        handleFilterChange() {
            this.currentPage = 1
        },
        // 搜索变化
        handleSearch() {
            this.currentPage = 1
        },
        // 每页条数变化
        handleSizeChange(val) {
            this.pageSize = val
            this.currentPage = 1
        },
        // 页码变化
        handleCurrentChange(val) {
            this.currentPage = val
            window.scrollTo(0, 0)
        },
        // 封面图加载失败处理
        handleCoverError(e) {
            e.target.src = 'https://picsum.photos/300/200?grayscale'
        },
        // 格式化发布时间
        formatTime(timeStr) {
            return timeStr ? timeStr.slice(0, 16) : '未知时间'
        },
        // 获取类别标签颜色
        getTypeColor(type) {
            const colorMap = {
                科技: '#409EFF',
                自然: '#52C41A',
                金融: '#FAAD14',
                体育: '#EB2F96',
                教育: '#722ED1'
            }
            return colorMap[type] || '#9CA3AF'
        }
    }
}
</script>

<style scoped>
.news-list-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: "Microsoft YaHei", sans-serif;
}

.page-title {
    margin-bottom: 30px;
}

.page-title h2 {
    font-size: 24px;
    color: #1F2937;
    margin: 0 0 8px 0;
}

.page-title .subtitle {
    font-size: 14px;
    color: #6B7280;
    margin: 0;
}

.filter-search-bar {
    display: flex;
    gap: 20px;
    margin-bottom: 25px;
    align-items: center;
    flex-wrap: wrap;
}

.filter-select {
    width: 200px;
}

.search-input {
    flex: 1;
    min-width: 300px;
}

.news-card-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.news-card {
    width: 100%;
    transition: all 0.3s ease;
    border-radius: 8px !important;
}

.news-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-2px);
}

.card-content {
    display: flex;
    gap: 20px;
    padding: 16px;
}

.news-cover-container {
    flex-shrink: 0;
    width: 300px;
    height: 180px;
    overflow: hidden;
    border-radius: 4px;
}

.news-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.news-card:hover .news-cover {
    transform: scale(1.05);
}

.news-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.news-title {
    font-size: 18px;
    font-weight: 600;
    color: #1F2937;
    margin: 0 0 12px 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.news-title a {
    color: #1F2937;
    text-decoration: none;
}

.news-title a:hover {
    color: #409EFF;
    text-decoration: underline;
}

.news-meta {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 14px;
    color: #6B7280;
}

.time-icon {
    margin-right: 4px;
    color: #9CA3AF;
}

.news-type-tag {
    padding: 2px 8px;
    border-radius: 12px;
    color: #fff;
    font-size: 12px;
}

.error-alert {
    margin-bottom: 25px;
}

.no-news-tip {
    padding: 80px 0;
}

.pagination-container {
    margin-top: 40px;
    text-align: center;
}

@media (max-width: 768px) {
    .card-content {
        flex-direction: column;
    }

    .news-cover-container {
        width: 100%;
        height: 200px;
    }

    .filter-search-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .filter-select,
    .search-input {
        width: 100%;
        min-width: auto;
    }

    .news-title {
        font-size: 16px;
    }
}
</style>